import { SetStateAction, Dispatch, useEffect, useState } from 'react'
import { FormType, PreRegistrationCustomProcess, TallyFieldType } from '../utils/sharedTypes'
import { Formtitle, Layout } from '../components'
import { Grid, Button, CircularProgress, Tabs, Tab, Box } from '@mui/material'
import { client } from '../utils/client'
import { useNavigate } from 'react-router-dom'
import { MdNavigateNext, MdNavigateBefore, MdRefresh } from 'react-icons/md'
import { BsCheck } from 'react-icons/bs'
import Bugsnag from '@bugsnag/js'

type TallyFormPropsType = {
  form: FormType
  setForm: Dispatch<SetStateAction<FormType>>
}

type SubmissionPayload = {
  id: string
  respondentId: string
  formId: string
  formName: string
  createdAt: Date
  fields: Array<{
    id: string
    title: string
    type:
      | 'INPUT_TEXT'
      | 'INPUT_NUMBER'
      | 'INPUT_EMAIL'
      | 'INPUT_PHONE_NUMBER'
      | 'INPUT_LINK'
      | 'INPUT_DATE'
      | 'INPUT_TIME'
      | 'TEXTAREA'
      | 'MULTIPLE_CHOICE'
      | 'DROPDOWN'
      | 'CHECKBOXES'
      | 'LINEAR_SCALE'
      | 'FILE_UPLOAD'
      | 'HIDDEN_FIELDS'
      | 'CALCULATED_FIELDS'
      | 'RATING'
      | 'MULTI_SELECT'
      | 'MATRIX'
      | 'RANKING'
      | 'SIGNATURE'
      | 'PAYMENT'
    answer: { value: unknown; raw: unknown }
  }>
}

type TabType = 'tally' | 'docusign'

// Component to display a Tally field
export const TallyField = ({ field }: { field: TallyFieldType }) => {
  const renderFieldValue = () => {
    if (!field.answer?.value) {
      return <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>Aucune valeur</span>
    }

    if (Array.isArray(field.answer.value)) {
      if (field.answer.value[0]?.name) {
        return field.answer.value[0]?.name
      }
    }

    return String(field.answer.value)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.75rem 0',
        borderBottom: '1px solid #f3f4f6',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <span
        style={{
          fontWeight: 500,
          color: '#374151',
          fontSize: '0.875rem',
          flex: 1,
        }}
      >
        {field.title}
      </span>
      <span
        style={{
          color: '#6b7280',
          fontSize: '0.875rem',
          fontWeight: 400,
          textAlign: 'right',
          maxWidth: '50%',
          wordBreak: 'break-word',
        }}
      >
        {renderFieldValue()}
      </span>
    </Box>
  )
}

const TallyForm = ({ form, setForm }: TallyFormPropsType) => {
  const navigate = useNavigate()
  const [currentProcessIndex, setCurrentProcessIndex] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [docusigns, setDocusigns] = useState<{ prcp_id: number; url: string }[]>([])
  const [selectedDocusignID, setSelectedDocusignID] = useState<number>()
  const [activeTab, setActiveTab] = useState<TabType>('tally')

  // Get active pre-registration processes
  const activeProcesses: PreRegistrationCustomProcess[] =
    form.data?.pre_registration_custom_processes?.filter((prcp) => prcp.active) || []

  // Get current process
  const currentProcess = activeProcesses[currentProcessIndex]

  // Check if there are multiple processes to handle
  const hasMultipleProcesses = activeProcesses.length > 1
  const isLastProcess = currentProcessIndex === activeProcesses.length - 1

  // Check if there are Tally and DocuSign forms
  const hasPublicTallyForms = currentProcess?.sales_process.sf_value === 'tally_form_public' ? true : false
  const hasPrivateTallyForms = currentProcess?.sales_process.sf_value === 'tally_form_private' ? true : false
  const hasDocuSignForms = currentProcess?.sales_process.sf_value === 'docusign' ? true : false

  // Check if form is completed
  const isFormCompleted = activeProcesses.every((p) => {
    return p.validated
  })

  // Function to check custom processes validation
  const checkCustomProcessesValidation = async () => {
    if (form.data.global_id) {
      try {
        setIsLoading(true)
        const resp = await client.get(`/registration/${form.data.global_id}/`)
        const data = resp.data as { custom_processes_validation?: boolean; [key: string]: unknown }

        // Update form with new data
        setForm((prevState) => ({
          ...prevState,
          data: {
            ...prevState.data,
            ...data,
          },
        }))
      } catch (err) {
        console.error('Error checking custom processes validation:', err)
        Bugsnag.notify(err as Error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  // Function to refresh pre-registration custom processes
  const refreshPRCP = async () => {
    try {
      setIsLoading(true)
      for await (const prcp of form.data.pre_registration_custom_processes) {
        const resp = await client.get(`/registration/pre_registration_custom_processes/${prcp.id}/`, {
          params: { registration_global_id: form.data.global_id },
        })
        if (resp.data) {
          setForm((prevState) => {
            return {
              ...prevState,
              data: {
                ...prevState.data,
                pre_registration_custom_processes: prevState.data.pre_registration_custom_processes.map((p) => {
                  if (p.id === prcp.id) {
                    return resp.data as PreRegistrationCustomProcess
                  }
                  return p
                }),
              },
            }
          })
        }
      }
      // Removed getDocusignURLS call to avoid double execution
      // The useEffect will handle DocuSign URL updates when needed
    } catch (err) {
      console.error('Error refreshing PRCP:', err)
    } finally {
      setIsLoading(false)
    }
  }

  // Function to get DocuSign process status summary
  const getDocuSignStatusSummary = () => {
    const docuSignProcesses = activeProcesses.filter((prcp) => prcp.sales_process.sf_value === 'docusign')

    if (docuSignProcesses.length === 0) {
      return { total: 0, validated: 0, pending: 0 }
    }

    const validated = docuSignProcesses.filter((prcp) => prcp.validated).length
    const pending = docuSignProcesses.length - validated

    return {
      total: docuSignProcesses.length,
      validated,
      pending,
    }
  }

  // Function to get DocuSign URLs
  const getDocusignURLS = async () => {
    // Clear existing DocuSign URLs
    setDocusigns([])

    // If we're on the DocuSign tab, only get URL for current process
    if (activeTab === 'docusign' && currentProcess?.sales_process.sf_value === 'docusign') {
      try {
        setIsLoading(true)
        console.log(`Getting DocuSign URL for current process ${currentProcess.id}`)

        const resp = await client.get(
          `/registration/pre_registration_custom_processes/${currentProcess.id}/get_or_create_docusign_envelope/`,
          { params: { registration_global_id: form.data.global_id } },
        )
        if (resp.data) {
          const envelopeUrl = resp.data as string
          console.log(`Got DocuSign URL for current process ${currentProcess.id}:`, envelopeUrl)

          // Add the DocuSign URL to the list
          setDocusigns([{ prcp_id: currentProcess.id, url: envelopeUrl }])
        }
      } catch (err) {
        console.error(`Error getting DocuSign URL for current process ${currentProcess.id}:`, err)
      } finally {
        setIsLoading(false)
      }
      return
    }
  }

  // Add "Formulaire" step to steps
  useEffect(() => {
    setForm((prevState) => {
      // Check if "Formulaire" step already exists
      const hasFormulaireStep = prevState.steps.some((step) => step.url === 'questionnaire')

      if (!hasFormulaireStep) {
        // Add "Formulaire" step before "Récapitulatif" step if it exists
        const recapitulatifIndex = prevState.steps.findIndex((step) => step.url === 'recapitulatif')
        const cpfIndex = prevState.steps.findIndex((step) => step.url === 'cpf')

        if (recapitulatifIndex !== -1) {
          const newSteps = [...prevState.steps]
          newSteps.splice(recapitulatifIndex, 0, {
            label: 'Questionnaire',
            url: 'questionnaire',
          })
          return {
            ...prevState,
            steps: newSteps,
          }
        } else if (cpfIndex !== -1) {
          const newSteps = [...prevState.steps]
          newSteps.splice(cpfIndex, 0, {
            label: 'Questionnaire',
            url: 'questionnaire',
          })
          return {
            ...prevState,
            steps: newSteps,
          }
        } else {
          // If no "Récapitulatif" step, add at the end
          return {
            ...prevState,
            steps: [
              ...prevState.steps,
              {
                label: 'Questionnaire',
                url: 'questionnaire',
              },
            ],
          }
        }
      }

      return prevState
    })
  }, [])

  // Check custom processes validation on load and after each change
  useEffect(() => {
    checkCustomProcessesValidation().catch(console.error)
  }, [])

  // Listen to DocuSign messages
  useEffect(() => {
    if (activeProcesses.length !== 0) {
      window.addEventListener('message', (event) => {
        if (event.data === 'DOCUSIGN_SIGNED_REFRESH') {
          // Only refresh if the current process is a DocuSign process
          if (currentProcess?.sales_process.sf_value === 'docusign') {
            refreshPRCP().catch(console.error)
          }
        }
      })
    }
  }, [activeProcesses, currentProcess])

  // Listen to DocuSign and Tally messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      console.log('event', event)

      // Handle Tally form loaded
      if (typeof event?.data === 'string' && event.data.includes('Tally.FormLoaded')) {
        try {
          const parsedData = JSON.parse(event.data) as { payload: { formId: string } }
          const payload = parsedData.payload
          console.log('Loaded form', payload)
        } catch (error) {
          console.error('Error parsing Tally.FormLoaded payload:', error)
        }
      }

      // Handle Tally form submitted
      if (typeof event?.data === 'string' && event.data.includes('Tally.FormSubmitted')) {
        try {
          const parsedData = JSON.parse(event.data) as { payload: SubmissionPayload }
          const payload = parsedData.payload
          console.log('Submitted form', payload)
          console.log('currentProcess', currentProcess)

          if (currentProcess) {
            setIsLoading(true)
            client
              .post(
                `/registration/pre_registration_custom_processes/${currentProcess.id}/tally_submission/`,
                {
                  tally_submission_id: payload.respondentId,
                  tally_form_id: payload.formId,
                  tally_submission_data: payload,
                },
                { params: { registration_global_id: form.data.global_id } },
              )
              .then((resp) => {
                console.log('Tally submission', resp)
                void checkCustomProcessesValidation()
              })
              .catch((err) => {
                console.error('Error submitting tally form:', err)
              })
              .finally(() => {
                setIsLoading(false)
              })
          }
        } catch (error) {
          console.error('Error parsing Tally.FormSubmitted payload:', error)
        }
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [currentProcess])

  // Select first DocuSign by default
  useEffect(() => {
    if (!selectedDocusignID && docusigns[0]) {
      setSelectedDocusignID(docusigns[0].prcp_id)
    }
  }, [docusigns])

  useEffect(() => {
    if (activeProcesses.length === 0) {
      return
    }

    if (form.data.pre_registration_custom_processes[currentProcessIndex].validated && !isLastProcess) {
      setCurrentProcessIndex(currentProcessIndex + 1)
    }
  }, [form.data.pre_registration_custom_processes])

  useEffect(() => {
    if (currentProcess?.sales_process.sf_value === 'tally_form_public') {
      setActiveTab('tally')
    } else if (currentProcess?.sales_process.sf_value === 'docusign') {
      setActiveTab('docusign')
    }
  }, [currentProcess])

  // Call getDocusignURLS when DocuSign tab becomes active
  useEffect(() => {
    if (activeTab === 'docusign' && currentProcess?.sales_process.sf_value === 'docusign') {
      console.log('DocuSign tab activated, getting URLs for current process')
      getDocusignURLS().catch((err) => {
        console.error('Error getting DocuSign URLs when tab activated:', err)
      })
    }
  }, [activeTab, currentProcess])

  const handleContinue = () => {
    client
      .get(`/registration/${form.data.global_id}/`)
      .then((resp) => {
        const data = resp.data as FormType['data']
        setForm((prev) => ({ ...prev, data: { ...prev.data, ...data } }))
        const code = data.funding_organism?.code
        if (data.positioning_status?.required) {
          navigate({ pathname: './../positionnement/', search: location.search })
        } else if (code === 'DPC') {
          navigate({ pathname: './../pre-inscription/', search: location.search })
        } else if (code === 'FIFPL') {
          navigate({ pathname: './../recapitulatif/', search: location.search })
        } else if (code === 'CPF') {
          navigate({ pathname: './../cpf/', search: location.search })
        } else {
          // FAFPM et autres : financement (budget/session) avant le récapitulatif
          navigate({ pathname: './../financement/', search: location.search })
        }
      })
      .catch((err: Error) => {
        Bugsnag.notify(err)
        console.error(err)
      })
  }

  // Tab change handler
  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabType) => {
    setActiveTab(newValue)
  }

  // If no active process with tally_form_id, show error message
  if (activeProcesses.length === 0) {
    return (
      <Layout form={form.data} steps={form.steps}>
        <Grid item container spacing={1} sx={{ justifyContent: 'flex-start' }}>
          <Formtitle title="Questionnaire" />
          <Grid item xs={12}>
            <p>Aucun questionnaire n'est à remplir dans le cadre de cette inscription.</p>
          </Grid>

          {/* Navigation buttons */}
          <Grid item sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              disabled={location.pathname.includes('/information')}
              sx={{
                height: {
                  xs: 45,
                },
              }}
              startIcon={<MdNavigateBefore />}
              onClick={() => {
                setForm((prevState) => {
                  return {
                    ...prevState,
                    steps: prevState.steps.filter((step) => step.url !== 'questionnaire'),
                  }
                })
                navigate({ pathname: './../information', search: location.search })
              }}
            >
              Retour
            </Button>
            <Button
              variant="contained"
              sx={{
                height: {
                  xs: 45,
                },
              }}
              endIcon={<MdNavigateNext />}
              onClick={() => {
                handleContinue()
              }}
            >
              Suivant
            </Button>
          </Grid>
        </Grid>
      </Layout>
    )
  }

  return (
    <Layout form={form.data} steps={form.steps}>
      <Grid item container spacing={1} sx={{ justifyContent: 'flex-start' }}>
        <Formtitle title="Questionnaire" />

        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            {/* Navigation buttons between Tally processes */}
            {activeProcesses.length > 0 && (
              <Grid item xs={12} sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  {activeProcesses.map((prcp, index) => (
                    <Button
                      key={prcp.id}
                      variant={currentProcessIndex === index ? 'contained' : 'outlined'}
                      onClick={() => setCurrentProcessIndex(index)}
                      sx={{
                        minWidth: '120px',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {form.data.products.find((p) => p.id === prcp.product)?.name ||
                        prcp.sales_process.display_name ||
                        prcp.sales_process.name}
                      {prcp.validated && (
                        <BsCheck
                          size="16px"
                          style={{
                            color: 'inherit',
                            marginLeft: '8px',
                          }}
                        />
                      )}
                    </Button>
                  ))}
                </Box>
              </Grid>
            )}
            {activeProcesses.some((p) => p.sales_process.sf_value == 'tally_form_private') && (
              <Button
                sx={{
                  height: {
                    xs: 45,
                  },
                  paddingX: 4,
                }}
                startIcon={<MdRefresh />}
                onClick={() => {
                  refreshPRCP()
                }}
              >
                Rafraîchir
              </Button>
            )}
          </Box>
        </Grid>

        {/* Tab navigation */}
        {hasPublicTallyForms && hasDocuSignForms && (
          <Grid item xs={12} sx={{ mb: 3 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="formulaires">
                {hasPublicTallyForms && <Tab label={`Questionnaire`} value="tally" disabled={!hasPublicTallyForms} />}
                {hasDocuSignForms && <Tab label="Documents à signer" value="docusign" disabled={!hasDocuSignForms} />}
              </Tabs>
            </Box>
          </Grid>
        )}

        {/* Tally tab content */}
        {activeTab === 'tally' && (
          <>
            {hasPublicTallyForms && (
              <>
                {activeProcesses[currentProcessIndex]?.validated ? (
                  // Display Tally submission data
                  <Grid container sx={{ position: 'relative', height: 'auto' }}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          backgroundColor: '#ffffff',
                          borderRadius: '12px',
                          border: '1px solid #e5e7eb',
                          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                          overflow: 'hidden',
                        }}
                      >
                        {/* Header with success icon */}
                        <Box
                          sx={{
                            backgroundColor: '#f0f9ff',
                            borderBottom: '1px solid #e5e7eb',
                            padding: '1.5rem',
                            textAlign: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '48px',
                              height: '48px',
                              borderRadius: '50%',
                              backgroundColor: '#10b981',
                              color: 'white',
                              marginBottom: '1rem',
                            }}
                          >
                            <BsCheck size="24px" />
                          </Box>
                          <h3
                            style={{
                              color: '#065f46',
                              margin: 0,
                              fontSize: '1.5rem',
                              fontWeight: 600,
                            }}
                          >
                            Questionnaire complété avec succès
                          </h3>
                        </Box>

                        {/* Content of data */}
                        {/*currentProcess && currentProcess.tally_submission_data && (
                      <Box sx={{ padding: '2rem' }}>                        
                        <Box>
                          <h4
                            style={{
                              fontSize: '1.125rem',
                              fontWeight: 600,
                              marginBottom: '1rem',
                              color: '#374151',
                              borderBottom: '2px solid #e5e7eb',
                              paddingBottom: '0.5rem',
                            }}
                          >
                            Vos réponses au questionnaire
                          </h4>
                          <Box
                            sx={{
                              backgroundColor: '#f9fafb',
                              borderRadius: '8px',
                              padding: '1rem',
                              border: '1px solid #f3f4f6',
                            }}
                          >
                            {currentProcess.tally_submission_data.fields
                              ?.filter((field: TallyFieldType) => field && field.type !== 'HIDDEN_FIELDS')
                              .map((field: TallyFieldType) => (
                                <TallyField key={field.id} field={field} />
                              ))}
                          </Box>
                        </Box>
                      </Box>
                    )*/}

                        {/* Navigation message */}
                        {hasMultipleProcesses && !isLastProcess && (
                          <Box
                            sx={{
                              backgroundColor: '#f0f9ff',
                              borderTop: '1px solid #e5e7eb',
                              padding: '1.5rem',
                              textAlign: 'center',
                            }}
                          >
                            <p
                              style={{
                                fontSize: '1rem',
                                color: '#0369a1',
                                margin: 0,
                                fontWeight: 500,
                              }}
                            >
                              Vous pouvez maintenant passer au questionnaire suivant
                            </p>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                ) : (
                  // Display Tally form
                  <Grid container sx={{ position: 'relative', height: 'auto' }}>
                    <Grid item xs={12}>
                      <iframe
                        key={`tally-iframe-${String(
                          activeProcesses[currentProcessIndex]?.tally_form_id,
                        )}-${currentProcessIndex}`}
                        src={`https://tally.so/embed/${String(
                          activeProcesses[currentProcessIndex]?.tally_form_id,
                        )}?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1&email=${form.data.email}`}
                        loading="lazy"
                        width="100%"
                        style={{
                          border: 'none',
                          height: '100%',
                          minHeight: '100dvh',
                          position: 'relative',
                        }}
                      ></iframe>
                    </Grid>
                  </Grid>
                )}
              </>
            )}
            {hasPrivateTallyForms && (
              <>
                {activeProcesses[currentProcessIndex]?.validated ? (
                  // Display Tally submission data
                  <Grid container sx={{ position: 'relative', height: 'auto' }}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          backgroundColor: '#ffffff',
                          borderRadius: '12px',
                          border: '1px solid #e5e7eb',
                          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                          overflow: 'hidden',
                        }}
                      >
                        {/* Header with success icon */}
                        <Box
                          sx={{
                            backgroundColor: '#f0f9ff',
                            borderBottom: '1px solid #e5e7eb',
                            padding: '1.5rem',
                            textAlign: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '48px',
                              height: '48px',
                              borderRadius: '50%',
                              backgroundColor: '#10b981',
                              color: 'white',
                              marginBottom: '1rem',
                            }}
                          >
                            <BsCheck size="24px" />
                          </Box>
                          <h3
                            style={{
                              color: '#065f46',
                              margin: 0,
                              fontSize: '1.5rem',
                              fontWeight: 600,
                            }}
                          >
                            Questionnaire complété avec succès
                          </h3>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                ) : (
                  // Display Tally form
                  <Grid container sx={{ position: 'relative', height: 'auto' }}>
                    <Grid item xs={12}>
                      <p>
                        Avant de continuer votre inscription, notre conseiller doit vous poser quelques questions
                        nécessaires pour la gestion administrative de votre dossier d'inscription.
                      </p>
                      <p>
                        Une fois vos réponses enregistrées par notre conseiller, cliquez sur le bouton en haut à droite
                        pour rafraîchir la page et accéder au bouton "Suivant" pour finaliser votre inscription.
                      </p>
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </>
        )}

        {/* DocuSign tab content */}
        {activeTab === 'docusign' && hasDocuSignForms && (
          <>
            {/* DocuSign status summary */}
            <Grid item xs={12} sx={{ mb: 3 }}>
              <Box
                sx={{
                  backgroundColor: '#f8fafc', // Fond par défaut pour toutes les zones
                  border: (() => {
                    const status = getDocuSignStatusSummary()
                    if (status.pending === 0 && status.validated > 0) {
                      return '1px solid #10b981' // Vert pour succès
                    } else if (status.pending > 0) {
                      return '1px solid #fbbf24' // Jaune pour en attente
                    } else {
                      return '1px solid #e2e8f0' // Gris par défaut
                    }
                  })(),
                  borderRadius: '8px',
                  padding: '1rem',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: getDocuSignStatusSummary().pending > 0 ? 2 : 0,
                  }}
                >
                  <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#374151' }}>
                    {(() => {
                      const status = getDocuSignStatusSummary()
                      if (status.pending === 0 && status.validated > 0) {
                        return '✅ Tous les documents sont signés'
                      } else if (status.pending > 0) {
                        return `📝 Documents à signer (${status.pending} en attente)`
                      } else {
                        return '📋 Aucun document DocuSign'
                      }
                    })()}
                  </h4>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={isLoading}
                    onClick={() => {
                      console.log('Refresh button clicked, calling refreshPRCP')
                      refreshPRCP().catch((err) => {
                        console.error(err)
                      })
                    }}
                  >
                    <MdRefresh size="16px" />
                    <span style={{ fontSize: '12px', marginLeft: 5 }}>
                      {isLoading ? 'Mise à jour...' : 'Rafraîchir'}
                    </span>
                  </Button>
                </Box>

                {/* Help message */}
                {(() => {
                  const status = getDocuSignStatusSummary()
                  if (status.pending > 0) {
                    return (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          backgroundColor: '#f8fafc', // Fond par défaut
                          borderRadius: '4px',
                          border: '1px solid #fbbf24', // Garde la bordure jaune pour l'attention
                        }}
                      >
                        <p style={{ margin: 0, fontSize: '0.875rem', color: '#92400e' }}>
                          💡 <strong>Conseil :</strong> Si vous avez déjà signé un document, cliquez sur "Rafraîchir"
                          pour mettre à jour le statut.
                        </p>
                      </Box>
                    )
                  }

                  return null
                })()}
              </Box>
            </Grid>

            {/* Pre-registration processes with DocuSign section */}

            {/* DocuSign section */}
            <Grid item xs={12}>
              <Grid
                sx={{
                  minHeight: '400px',
                  display: 'flex',
                  flex: 1,
                  position: 'relative',
                  justifyContent: 'center',
                }}
              >
                {isLoading ? (
                  <div
                    style={{
                      flex: 1,
                      height: '100%',
                      justifyContent: 'center',
                      display: 'flex',
                    }}
                  >
                    <CircularProgress />
                  </div>
                ) : (
                  <div
                    style={{
                      height: '100%',
                      justifyContent: 'center',
                      display: 'flex',
                    }}
                  >
                    {docusigns.find((ds) => ds.prcp_id === selectedDocusignID)?.url && (
                      <iframe
                        id={`${selectedDocusignID ?? ''}`}
                        key={selectedDocusignID}
                        src={docusigns.find((ds) => ds.prcp_id === selectedDocusignID)?.url}
                        width="100%"
                        height="400px"
                        title="DocuSign Embedded Signing"
                        style={{ top: 'unset', bottom: 'unset', border: 'none' }}
                      />
                    )}
                  </div>
                )}
              </Grid>
            </Grid>
          </>
        )}

        {/* Navigation buttons */}
        <Grid item sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            disabled={location.pathname.includes('/information')}
            sx={{
              height: {
                xs: 45,
              },
            }}
            startIcon={<MdNavigateBefore />}
            onClick={() => {
              setForm((prevState) => {
                return {
                  ...prevState,
                  steps: prevState.steps.filter((step) => step.url !== 'questionnaire'),
                }
              })

              navigate({ pathname: './../information', search: location.search })
            }}
          >
            Retour
          </Button>
          <Button
            variant="contained"
            disabled={!isFormCompleted}
            sx={{
              height: {
                xs: 45,
              },
            }}
            endIcon={<MdNavigateNext />}
            onClick={handleContinue}
          >
            Suivant
          </Button>
        </Grid>
      </Grid>
    </Layout>
  )
}

export default TallyForm
